import type {
  CrmContactHealthMetric,
  CrmContactHealthSnapshot,
  CrmContactHealthTone,
} from "@ease/crm/contact-health-snapshot";
import type {
  CrmContactProgressMilestone,
  CrmContactProgressSnapshot,
  CrmContactProgressTone,
} from "@ease/crm/contact-progress-snapshot";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/i18n";

interface CrmHealthSnapshotCardProps {
  snapshot: CrmContactHealthSnapshot | null;
  title?: string;
  description?: string;
  className?: string;
}

interface CrmProgressSnapshotCardProps {
  snapshot: CrmContactProgressSnapshot | null;
  title?: string;
  description?: string;
  className?: string;
}

export function CrmHealthSnapshotCard({
  snapshot,
  title = "Health snapshot",
  description = "Tenant-configured customer health, retention, onboarding, or success evidence.",
  className,
}: CrmHealthSnapshotCardProps) {
  if (!snapshot) return null;

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {snapshot.score !== null ? (
            <span className={cn(
              "rounded-full border px-2.5 py-1 text-xs font-semibold",
              getCrmHealthToneClasses(snapshot.tone),
            )}>
              {snapshot.score} / 100
            </span>
          ) : null}
          {snapshot.status ? (
            <span className="text-xs text-[var(--color-muted-foreground)]">{snapshot.status.replaceAll("_", " ")}</span>
          ) : null}
        </div>
      </div>
      {snapshot.summary ? (
        <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">{snapshot.summary}</p>
      ) : null}
      {snapshot.score !== null ? (
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-muted)]" role="progressbar" aria-label={`${snapshot.label} health score ${snapshot.score} of 100`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={snapshot.score}>
          <div className={cn("h-full rounded-full", getCrmHealthMetricBarClasses(snapshot.tone))} style={{ width: `${snapshot.score}%` }} />
        </div>
      ) : null}
      {snapshot.metrics.length > 0 ? (
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          {snapshot.metrics.map((metric) => (
            <HealthMetricCell key={`${snapshot.key}:${metric.key}`} metric={metric} />
          ))}
        </div>
      ) : null}
      {snapshot.updatedAt ? (
        <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">Updated {formatDate(snapshot.updatedAt)}</p>
      ) : null}
    </section>
  );
}

function HealthMetricCell({ metric }: { metric: CrmContactHealthMetric }) {
  const percent = readCrmSnapshotMetricPercent(metric.value);

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase text-[var(--color-muted-foreground)]">{metric.label}</p>
          {metric.weight ? (
            <p className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
              Weight {metric.weight}
            </p>
          ) : null}
        </div>
        {metric.tone !== "neutral" ? (
          <span className={cn(
            "shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
            getCrmHealthToneClasses(metric.tone),
          )}>
            {metric.tone}
          </span>
        ) : null}
      </div>
      <p className="mt-1 break-words text-sm text-[var(--color-foreground)]">{metric.value}</p>
      {percent !== null ? (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--color-muted)]" role="progressbar" aria-label={`${metric.label} ${metric.value}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
          <div className={cn("h-full rounded-full", getCrmHealthMetricBarClasses(metric.tone))} style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      {metric.description ? (
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{metric.description}</p>
      ) : null}
    </div>
  );
}

export function CrmProgressSnapshotCard({
  snapshot,
  title = "Progress snapshot",
  description = "Tenant-configured learning, onboarding, course, lifecycle, or success progress.",
  className,
}: CrmProgressSnapshotCardProps) {
  if (!snapshot) return null;

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {snapshot.progressPercent !== null ? (
            <span className={cn(
              "rounded-full border px-2.5 py-1 text-xs font-semibold",
              getCrmProgressToneClasses(snapshot.tone),
            )}>
              {snapshot.progressPercent}%
            </span>
          ) : null}
          {snapshot.status ? (
            <span className="text-xs text-[var(--color-muted-foreground)]">{snapshot.status.replaceAll("_", " ")}</span>
          ) : null}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {snapshot.programLabel ? (
          <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-0.5 text-xs text-[var(--color-muted-foreground)]">
            {snapshot.programLabel}
          </span>
        ) : null}
        <span className={cn(
          "rounded-full border px-2 py-0.5 text-xs font-medium",
          getCrmProgressToneClasses(snapshot.tone),
        )}>
          {snapshot.label}
        </span>
      </div>
      {snapshot.summary ? (
        <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">{snapshot.summary}</p>
      ) : null}
      {snapshot.progressPercent !== null ? (
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-muted)]" role="progressbar" aria-label={`${snapshot.label} progress ${snapshot.progressPercent} of 100`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={snapshot.progressPercent}>
          <div className={cn("h-full rounded-full", getCrmProgressMetricBarClasses(snapshot.tone))} style={{ width: `${snapshot.progressPercent}%` }} />
        </div>
      ) : null}
      {snapshot.milestones.length > 0 ? (
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          {snapshot.milestones.map((milestone) => (
            <ProgressMilestoneCell key={`${snapshot.key}:${milestone.key}`} milestone={milestone} />
          ))}
        </div>
      ) : null}
      {snapshot.updatedAt ? (
        <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">Updated {formatDate(snapshot.updatedAt)}</p>
      ) : null}
    </section>
  );
}

function ProgressMilestoneCell({ milestone }: { milestone: CrmContactProgressMilestone }) {
  const percent = milestone.value ? readCrmSnapshotMetricPercent(milestone.value) : null;

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase text-[var(--color-muted-foreground)]">{milestone.label}</p>
          {milestone.status ? (
            <p className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
              {milestone.status.replaceAll("_", " ")}
            </p>
          ) : null}
        </div>
        {milestone.tone !== "neutral" ? (
          <span className={cn(
            "shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
            getCrmProgressToneClasses(milestone.tone),
          )}>
            {milestone.tone}
          </span>
        ) : null}
      </div>
      {milestone.value ? (
        <p className="mt-1 break-words text-sm text-[var(--color-foreground)]">{milestone.value}</p>
      ) : null}
      {percent !== null ? (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--color-muted)]" role="progressbar" aria-label={`${milestone.label} ${milestone.value}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
          <div className={cn("h-full rounded-full", getCrmProgressMetricBarClasses(milestone.tone))} style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      {milestone.description ? (
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{milestone.description}</p>
      ) : null}
      {milestone.completedAt || milestone.dueAt ? (
        <p className="mt-2 text-[10px] uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
          {milestone.completedAt ? `Completed ${formatDate(milestone.completedAt)}` : `Due ${formatDate(milestone.dueAt!)}`}
        </p>
      ) : null}
    </div>
  );
}

export function readCrmSnapshotMetricPercent(value: string): number | null {
  const match = /^(\d{1,3}(?:\.\d+)?)(?:%)?$/.exec(value.trim());
  const rawPercent = match?.[1];
  if (!rawPercent) return null;
  const percent = Number.parseFloat(rawPercent);
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) return null;
  return percent;
}

export function getCrmHealthMetricBarClasses(tone: CrmContactHealthTone): string {
  if (tone === "success") return "bg-[var(--color-success)]";
  if (tone === "warning") return "bg-[var(--color-warning)]";
  if (tone === "danger") return "bg-[var(--color-destructive)]";
  return "bg-[var(--color-primary)]";
}

export function getCrmProgressMetricBarClasses(tone: CrmContactProgressTone): string {
  if (tone === "success") return "bg-[var(--color-success)]";
  if (tone === "warning") return "bg-[var(--color-warning)]";
  if (tone === "danger") return "bg-[var(--color-destructive)]";
  return "bg-[var(--color-primary)]";
}

export function getCrmHealthToneClasses(tone: CrmContactHealthTone) {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-info)]/30 bg-[var(--color-info)]/10 text-[var(--color-info)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}

export function getCrmProgressToneClasses(tone: CrmContactProgressTone) {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-info)]/30 bg-[var(--color-info)]/10 text-[var(--color-info)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}
